// Fuzzy plate matching for display/enrichment only (notes, categories on the
// statistics page). Never used for gate-open decisions — Dahua cameras already
// do their own onboard fuzzy matching for that, correctly. This just closes the
// gap where our own "which enrolled plate does this OCR'd text correspond to"
// lookup was exact-only, so a plate the camera happily opened for showed up
// with no note/category attached.

// Known OCR confusions on these cameras — always treated as equal, no budget cost.
const EQUIVALENCE_GROUPS = [
  ['I', '1'],
  ['O', '0', 'D', 'Q'],
  ['X', 'K'],
  ['B', '8'],
];

const CANONICAL = {};
EQUIVALENCE_GROUPS.forEach((group) => {
  group.forEach((ch) => { CANONICAL[ch] = group[0]; });
});

const canon = (ch) => CANONICAL[ch] || ch;

export const normalizePlate = (plate) => (plate ?? '').replace(/[^A-Z0-9]/gi, '').toUpperCase();

/**
 * Counts mismatched positions between two equal-length normalized strings,
 * treating known OCR-confusable characters (see EQUIVALENCE_GROUPS) as free.
 * Returns null if the strings aren't the same length.
 */
function mismatchCount(a, b) {
  if (a.length !== b.length) return null;
  let mismatches = 0;
  for (let i = 0; i < a.length; i++) {
    if (canon(a[i]) !== canon(b[i])) mismatches++;
  }
  return mismatches;
}

/**
 * Lowest mismatch count between `ocr` and `enrolled`, allowing at most one
 * inserted/dropped character at either edge (OCR misreading the plate as one
 * character longer or shorter than it really is). Returns Infinity if the
 * strings differ in length by more than one character.
 */
export function plateDistance(enrolled, ocr) {
  const a = normalizePlate(enrolled);
  const b = normalizePlate(ocr);
  const lenDiff = a.length - b.length;

  if (lenDiff === 0) {
    return mismatchCount(a, b) ?? Infinity;
  }
  if (Math.abs(lenDiff) !== 1) {
    return Infinity;
  }

  // whichever string is longer, try dropping its first or last character to
  // align lengths, and keep whichever alignment matches better
  const [longer, shorter] = lenDiff > 0 ? [a, b] : [b, a];
  const dropFirst = mismatchCount(longer.slice(1), shorter);
  const dropLast = mismatchCount(longer.slice(0, -1), shorter);
  return Math.min(dropFirst ?? Infinity, dropLast ?? Infinity);
}

/**
 * Finds the best-matching plate object from `candidates` (each needs a `.plate`
 * string) for a raw OCR'd plate string, allowing up to `maxMisreads` characters
 * to differ beyond the known-confusable set. Returns null if nothing is close
 * enough. Exact/equivalence-only matches (distance 0) always win over anything
 * that needed the misread budget.
 */
export function findFuzzyPlateMatch(ocrPlateNo, candidates, maxMisreads = 1) {
  let best = null;
  let bestDistance = Infinity;

  for (const candidate of candidates) {
    const distance = plateDistance(candidate.plate, ocrPlateNo);
    if (distance <= maxMisreads && distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
      if (bestDistance === 0) break; // can't do better than an exact/equivalence match
    }
  }

  return best ? { candidate: best, distance: bestDistance } : null;
}
